/**
 * Round 12 #727 — the CORE component set (rounds/12/design-rules.md, the mock's style-guide tab).
 * Thin wrappers over the `.core-*` classes in src/index.css: every colour is a token, every label
 * is the caller's (stage names come from the stage rows, menus from GET /api/apps). Shared with the
 * other UI lanes (L2's request page, L7's portal cards) — import from '@/components/core'.
 */
import { FeedbackPill } from '@/kit';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { calendarDaysBetween, formatDate, formatRelative, RELATIVE_DAYS } from '@/lib/format';
import { usePageEyebrow } from '@/app/shell/page-eyebrow';

/** Page head: eyebrow · h1 · one-line subtitle, the actions at the right (one primary, a secondary or an arrowhead link). */
export function PageHead({ eyebrow: own, title, subtitle, actions, className }: { eyebrow?: ReactNode; title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; className?: string }) {
  const shell = usePageEyebrow();
  const eyebrow = own ?? shell;
  return (
    <header className={cn('mb-4 flex flex-wrap items-end justify-between gap-4', className)} data-testid="page-head">
      <div className="min-w-0">
        {eyebrow ? <p className="eyebrow mb-1.5">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {subtitle ? <div className="mt-1 max-w-3xl text-[length:var(--t-body)] text-muted-foreground">{subtitle}</div> : null}
      </div>
      {/* R-15 D2 (T-111): the feedback pill on every page head */}
      <div className="flex shrink-0 flex-wrap items-center gap-2.5">
        {actions}
        <FeedbackPill />
      </div>
    </header>
  );
}

/**
 * The page column. R-13 (design system v1.0, Decision 1): full width, no max-width — the kit
 * PageShell's content region already carries the one gutter (--gutter) and the vertical rhythm, so
 * a route that keeps `Page` is conformant as it stands.
 */
export function Page({ children, className, ...rest }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div data-kit="page" className={cn('w-full min-w-0', className)} {...rest}>
      {children}
    </div>
  );
}

/** Layer 2: a resting card. `primary` adds the mitered corner (the page's primary card only, never a container of cards). */
export function CoreCard({ primary, className, children, ...rest }: ComponentPropsWithoutRef<'section'> & { primary?: boolean }) {
  return (
    <section className={cn('core-card', primary && 'core-miter', className)} data-rank={primary ? 'primary' : undefined} {...rest}>
      {children}
    </section>
  );
}

/** A rail card's caps heading ("Waiting for you"). */
export function RailTitle({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <h3 className="core-rail-title" id={id}>
      {children}
    </h3>
  );
}

/** Tiles, not label/value lines: at most three per row; the first highlighted is the one the role acts on. */
export function Tiles({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('core-tiles', className)}>{children}</div>;
}

export function Tile({ n, label, highlight, to, testId }: { n: ReactNode; label: ReactNode; highlight?: boolean; to?: string; testId?: string }) {
  const body = (
    <>
      <div className="core-tile-n">{n}</div>
      <div className="core-tile-l">{label}</div>
    </>
  );
  return to ? (
    <Link to={to} className="core-tile hover:no-underline" data-highlight={highlight ? 'true' : undefined} data-testid={testId}>
      {body}
    </Link>
  ) : (
    <div className="core-tile" data-highlight={highlight ? 'true' : undefined} data-testid={testId}>
      {body}
    </div>
  );
}

export type StageTone = 'requested' | 'described' | 'reviewed' | 'approved' | 'live' | 'closed';
const TONE_BY_ORDER: readonly StageTone[] = ['requested', 'described', 'reviewed', 'approved', 'reviewed', 'live'];

/**
 * A stage's colour pair. The tone follows the stage's code when it names one of the five painted
 * stages, else its position in the pipeline; Building uses the Reviewed pair until the mock says
 * otherwise (labelled shortcut, ADR-027). The LABEL is never derived here — it is the stage row's.
 */
export function stageTone(code: string | null | undefined, order?: number | null): StageTone {
  const c = (code ?? '').toLowerCase();
  if (/(^|_)(rejected|withdrawn|closed|cancel)/.test(c)) return 'closed';
  if (c.includes('live') || c.includes('done') || c.includes('released')) return 'live';
  if (c.includes('approved')) return 'approved';
  if (c.includes('build') || c.includes('review')) return 'reviewed';
  if (c.includes('describ')) return 'described';
  if (c.includes('request') || c.includes('draft') || c.includes('submitted')) return 'requested';
  return TONE_BY_ORDER[Math.max(0, Math.min(TONE_BY_ORDER.length - 1, order ?? 0))]!;
}

export function StagePill({ label, code, order, className }: { label: string; code?: string | null; order?: number | null; className?: string }) {
  return (
    <span className={cn('core-stage', className)} data-tone={stageTone(code, order)} data-stage={code ?? undefined}>
      {label}
    </span>
  );
}

/** Chips: ok (Live) · warn (Gap) · bad · neutral · brand; `dot` where it is a status. */
export function Chip({ tone, dot, children, className }: { tone?: 'ok' | 'warn' | 'bad' | 'brand'; dot?: boolean; children: ReactNode; className?: string }) {
  return (
    <span className={cn('core-chip', className)} data-tone={tone}>
      {dot ? <i className="core-chip-dot" aria-hidden /> : null}
      {children}
    </span>
  );
}

/** The arrowhead CTA link (▶ Label). */
export function CtaLink({ to, children, className, ...rest }: { to: string; children: ReactNode; className?: string } & Omit<ComponentPropsWithoutRef<typeof Link>, 'to'>) {
  return (
    <Link to={to} className={cn('core-cta', className)} {...rest}>
      {children}
    </Link>
  );
}

/** Ghost card ("Coming next"): what is not yet there, with one arrowhead action. */
export function GhostCard({ eyebrow, title, children, action, testId }: { eyebrow?: ReactNode; title: ReactNode; children?: ReactNode; action?: ReactNode; testId?: string }) {
  return (
    <section className="core-ghost" data-testid={testId}>
      {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
      <h3 className="core-section-title">{title}</h3>
      {children ? <div className="text-[length:var(--t-control)]">{children}</div> : null}
      {action ? <div>{action}</div> : null}
    </section>
  );
}

/** A section header inside a rail or a column: tracked caps with a hairline above. */
export function SectionLabel({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <li className="core-section-label list-none" id={id} role="presentation">
      {children}
    </li>
  );
}

/** A selectable entry (Admin rail, Model columns): resting card → hover tint → raised with the accent rail when selected. */
export function RailItem({ to, title, sub, selected, testId, children }: { to?: string; title: ReactNode; sub?: ReactNode; selected?: boolean; testId?: string; children?: ReactNode }) {
  const body = (
    <>
      <span className="core-item-title">{title}</span>
      {sub ? <span className="core-item-sub">{sub}</span> : null}
      {children}
    </>
  );
  return (
    <li className="mb-2 list-none">
      {to ? (
        <Link to={to} className="core-item hover:no-underline" data-selected={selected ? 'true' : undefined} aria-current={selected ? 'page' : undefined} data-testid={testId}>
          {body}
        </Link>
      ) : (
        <div className="core-item" data-selected={selected ? 'true' : undefined} data-testid={testId}>
          {body}
        </div>
      )}
    </li>
  );
}

/** Drawer furniture: the kv grid (150px labels in ink-3) and the note block. */
export function Kv({ rows, className }: { rows: readonly [ReactNode, ReactNode][]; className?: string }) {
  return (
    <dl className={cn('core-kv mt-2.5', className)}>
      {rows.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Note({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('core-note mt-3', className)}>{children}</div>;
}

/** The key line under a title ("Data change request DCR-0012 · version 1"). */
export function KeyLine({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('core-key', className)}>{children}</div>;
}

/** A dated list (What's new, activity): when · what · who. */
export function DatedList({ rows, empty }: { rows: readonly { id: string; when: ReactNode; what: ReactNode; who?: ReactNode }[]; empty?: ReactNode }) {
  if (rows.length === 0) return empty ? <p className="text-[length:var(--t-control)] text-muted-foreground">{empty}</p> : null;
  return (
    <ul className="core-list m-0 list-none p-0">
      {rows.map((r) => (
        <li key={r.id}>
          {r.when ? <span className="core-when">{r.when}</span> : null}
          <span className="min-w-0">{r.what}</span>
          {r.who ? <span className="core-who">{r.who}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/** "Yesterday", "Mon", "Oct 9": the mock's `when` column, in the reader's locale. */
export function when(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = calendarDaysBetween(d, now); // #87 4382576426: calendar days in the user's zone
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return diff <= RELATIVE_DAYS ? formatRelative(d, now.getTime()) : formatDate(d);
}

/**
 * Round 12 #746 (L3 half) — "Recorded · <receipt>" beside a decision: the decision record's receipt state and
 * id as the API gives them (receiptState / receiptId; the older receipt_state / receipt_hash read the same).
 * A field, not a verify control. Nothing recorded yet reads so; an unknown state shows its own word.
 */
export function ReceiptIndicator({ decision }: { decision: { receiptState?: string | null; receiptId?: string | null; receipt_state?: string | null; receipt_hash?: string | null } | null | undefined }) {
  const state = (decision?.receiptState ?? decision?.receipt_state ?? '').toLowerCase();
  const id = decision?.receiptId ?? (decision?.receipt_hash ? decision.receipt_hash.slice(0, 12) : null);
  if (!decision || !state || state === 'none') return <span className="core-key" data-testid="receipt-indicator" data-receipt-state="none">Not recorded yet</span>;
  const recorded = state === 'recorded' || state === 'sealed';
  return (
    <Chip tone={recorded ? 'ok' : undefined} dot className="align-middle">
      <span data-testid="receipt-indicator" data-receipt-state={state}>
        {recorded ? 'Recorded' : state.charAt(0).toUpperCase() + state.slice(1)}
        {id ? <> · <span className="font-mono">{id}</span></> : null}
      </span>
    </Chip>
  );
}
